import React, { useEffect, useState } from "react";
import { api, formatDetail, API } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, EmptyState } from "@/components/ui-parts";
import { Plus, Building2, Pencil, Trash2, X, FileDown } from "lucide-react";
import { toast } from "sonner";

const defaultColors = ["#18322B", "#2A4A6B", "#B24A0F", "#5C3B7A", "#0E5A4A", "#7A5C1F"];

function CompanyForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    legal_form: initial?.legal_form || "",
    tax_id: initial?.tax_id || "",
    iban: initial?.iban || "",
    keywords: (initial?.keywords || []).join(", "),
    aliases: (initial?.aliases || []).join(", "),
    business_purpose: initial?.business_purpose || "",
    typical_categories: (initial?.typical_categories || []).join(", "),
    color: initial?.color || defaultColors[0],
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      typical_categories: form.typical_categories.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await onSave(payload);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E6E6E4] rounded-md p-6 space-y-4" data-testid="company-form">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-lg text-[#1A1A1A]">
          {initial ? "Firma bearbeiten" : "Neue Firma"}
        </div>
        <button type="button" onClick={onCancel} className="text-[#5C5E60] hover:text-[#1A1A1A]" data-testid="company-form-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Firmenname *</label>
          <input data-testid="company-input-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Rechtsform</label>
          <input data-testid="company-input-legalform" value={form.legal_form} onChange={(e) => setForm({ ...form, legal_form: e.target.value })}
            placeholder="GmbH, e.K., ..." className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Steuernummer / USt-ID</label>
          <input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">IBAN</label>
          <input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono-tab focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Schlüsselwörter (Komma-getrennt)</label>
          <input data-testid="company-input-keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}
            placeholder="Amazon, Büromaterial, IT"
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
          <div className="text-[11px] text-[#5C5E60] mt-1">Helfen der KI, Belege der richtigen Firma zuzuordnen.</div>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Alternative Namen / Aliase (Komma-getrennt)</label>
          <input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })}
            placeholder="Meine Firma GmbH, MF Handel"
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Geschäftszweck / Branche</label>
          <textarea
            data-testid="company-input-business-purpose"
            value={form.business_purpose}
            onChange={(e) => setForm({ ...form, business_purpose: e.target.value })}
            rows={2}
            placeholder="z.B. Handwerksbetrieb, Bauleistungen, Immobilienverwaltung"
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
          <div className="text-[11px] text-[#5C5E60] mt-1">Der KI-Assistent nutzt diesen Text, um zu erkennen, welche Ausgaben plausibel sind.</div>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Typische Kategorien (Komma-getrennt)</label>
          <input
            data-testid="company-input-typical-cats"
            value={form.typical_categories}
            onChange={(e) => setForm({ ...form, typical_categories: e.target.value })}
            placeholder="Baumaterial, Werkzeug, Kraftstoff, Büromaterial"
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" />
          <div className="text-[11px] text-[#5C5E60] mt-1">Kategorien dieser Firma. Belege in diesen Kategorien werden nicht hinterfragt.</div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Farbe</label>
          <div className="flex gap-1.5 flex-wrap">
            {defaultColors.map((c) => (
              <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                className={`h-8 w-8 rounded-md border-2 transition-colors duration-150 ${form.color === c ? "border-[#1A1A1A]" : "border-transparent"}`}
                style={{ background: c }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <SecondaryButton type="button" onClick={onCancel} data-testid="company-form-cancel">Abbrechen</SecondaryButton>
        <PrimaryButton type="submit" disabled={saving} data-testid="company-form-save">
          {saving ? "Speichern..." : "Speichern"}
        </PrimaryButton>
      </div>
    </form>
  );
}

export default function Companies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [exportFor, setExportFor] = useState(null); // company being exported

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setItems(data);
    } catch { toast.error("Firmen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (payload) => {
    if (editing) {
      await api.patch(`/companies/${editing.id}`, payload);
      toast.success("Firma aktualisiert");
    } else {
      await api.post("/companies", payload);
      toast.success("Firma angelegt");
    }
    setEditing(null);
    setShowForm(false);
    load();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Firma "${c.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Firma gelöscht");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div>
      <PageHeader
        title="Firmen"
        subtitle="Verwalten Sie Ihre Firmen. Schlüsselwörter verbessern die automatische Beleg-Zuordnung."
        actions={
          !showForm && (
            <PrimaryButton onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-company-btn">
              <Plus className="h-4 w-4" strokeWidth={2} /> Neue Firma
            </PrimaryButton>
          )
        }
      />

      <div className="px-8 py-8 space-y-6">
        {showForm && (
          <CompanyForm
            initial={editing}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        )}

        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade...</div>
        ) : items.length === 0 && !showForm ? (
          <EmptyState
            title="Noch keine Firmen"
            hint="Legen Sie Ihre erste Firma an, um Belege und Kontoauszüge zuzuordnen."
            action={
              <PrimaryButton onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4" /> Erste Firma anlegen
              </PrimaryButton>
            }
            testid="companies-empty"
          />
        ) : (
          <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="companies-list">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8] border-b border-[#E6E6E4]">
                  <th className="text-left px-6 py-3 font-semibold">Firma</th>
                  <th className="text-left px-4 py-3 font-semibold">Rechtsform</th>
                  <th className="text-left px-4 py-3 font-semibold">USt-ID</th>
                  <th className="text-left px-4 py-3 font-semibold">Schlüsselwörter</th>
                  <th className="text-right px-6 py-3 font-semibold">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-b border-[#E6E6E4] last:border-b-0 hover:bg-[#F9F9F8] transition-colors duration-150">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color || "#18322B" }} />
                        <span className="font-medium text-[#1A1A1A]">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#5C5E60]">{c.legal_form || "—"}</td>
                    <td className="px-4 py-3 font-mono-tab text-[#5C5E60]">{c.tax_id || "—"}</td>
                    <td className="px-4 py-3 text-[#5C5E60]">
                      {(c.keywords || []).slice(0, 4).map((k) => (
                        <span key={k} className="inline-block mr-1 mb-1 text-[11px] bg-[#F3F3F1] rounded px-1.5 py-0.5">{k}</span>
                      ))}
                      {(c.keywords || []).length === 0 && "—"}
                    </td>
                    <td className="text-right px-6 py-3">
                      <button data-testid={`export-company-${c.id}`} onClick={() => setExportFor(c)}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#18322B] transition-colors duration-150 mr-1">
                        <FileDown className="h-3.5 w-3.5" strokeWidth={1.75} /> Monats-Export
                      </button>
                      <button data-testid={`edit-company-${c.id}`} onClick={() => { setEditing(c); setShowForm(true); }}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] transition-colors duration-150 mr-1">
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} /> Bearbeiten
                      </button>
                      <button data-testid={`delete-company-${c.id}`} onClick={() => handleDelete(c)}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D] transition-colors duration-150">
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {exportFor && (
        <MonthlyExportModal
          company={exportFor}
          onClose={() => setExportFor(null)}
        />
      )}
    </div>
  );
}


function MonthlyExportModal({ company, onClose }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-based
  const [busy, setBusy] = useState(false);

  const months = ["Januar","Februar","März","April","Mai","Juni",
                  "Juli","August","September","Oktober","November","Dezember"];
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(
        `${API}/companies/${company.id}/monthly-export?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: "Fehler" }));
        throw new Error(formatDetail(err.detail) || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vorbuch_${company.name.replace(/ /g, "_")}_${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Monats-PDF heruntergeladen");
      onClose();
    } catch (e) {
      toast.error(e.message || "Export fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="monthly-export-modal">
      <div className="bg-white rounded-md w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg text-[#1A1A1A]">Monats-Export</div>
            <div className="text-xs text-[#5C5E60] mt-0.5">
              {company.name} · alle Auszüge + Belege
            </div>
          </div>
          <button onClick={onClose} className="text-[#5C5E60] hover:text-[#1A1A1A]" data-testid="close-monthly-export">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Monat</label>
            <select data-testid="export-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white hover:border-[#18322B]">
              {months.map((n, i) => (
                <option key={i + 1} value={i + 1}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Jahr</label>
            <select data-testid="export-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white hover:border-[#18322B]">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <p className="text-[11px] text-[#5C5E60] leading-relaxed">
          Es werden alle Kontoauszüge dieser Firma einbezogen, deren Zeitraum in
          den gewählten Monat fällt. Jeder Auszug wird mit den zugeordneten
          Belegen und Platzhaltern für fehlende Belege ausgeliefert. Eine
          Deckseite listet alle enthaltenen Auszüge auf.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton onClick={onClose} data-testid="cancel-monthly-export">Abbrechen</SecondaryButton>
          <PrimaryButton onClick={download} disabled={busy} data-testid="run-monthly-export">
            <FileDown className="h-4 w-4" /> {busy ? "Erstelle PDF…" : "PDF erstellen"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
